import {Service} from "@angular/core";
import type {IJsonObject} from "dcerialize";
import type {Observable} from "rxjs";
import {mapToIdSelectOptions} from "../../helpers/RxJsOperators";
import type {IListParameters, IPaginatedListParameters} from "../../models/base/ListParameters";
import type {Paginated} from "../../models/base/Paginated";
import type {SelectOption} from "../../models/base/SelectOptions";
import {SortBy} from "../../models/base/SortCriteria";
import type {Translations} from "../../models/base/Translations";
import type {CountryCode} from "../../models/i18n/Country";
import {Country, CountryTranslation, SimpleCountry} from "../../models/i18n/Country";
import type {LanguageCode} from "../../models/i18n/Language";
import {Language, LanguageTranslation, SimpleLanguage} from "../../models/i18n/Language";
import {RequestBuilder} from "../../models/RequestBuilder";
import {BaseApi} from "./base-api";

@Service()
export class I18nApi extends BaseApi {
  public constructor() {
    super("i18n");
  }

  public listLanguages(
    translatedInItself: boolean = false,
    params: IPaginatedListParameters<Language> = {},
  ): Observable<Paginated<Language>> {
    const request = RequestBuilder.get("languages")
      .authenticate()
      .withQueryParameter("translated-in-language", translatedInItself)
      .withPaginatedListParameters(params)
      .expectJsonObject();
    return this.execute(request).notNull().decodePaginatedOf(Language);
  }

  private listSimpleLanguagesRequest(
    translatedInItself: boolean,
    params: IPaginatedListParameters<SimpleLanguage>): RequestBuilder<undefined, IJsonObject> {
    return RequestBuilder.get("languages", "simple")
      .withAuthorizationOptions({
        needsAuthentication: !translatedInItself, // If translatedInItself is true, we don't need authentication
      })
      .withQueryParameter("translated-in-language", translatedInItself)
      .withPaginatedListParameters(params)
      .expectJsonObject();
  }

  public listSimpleLanguages(
    translatedInItself: boolean = false,
    params: IPaginatedListParameters<SimpleLanguage> = {},
  ): Observable<Paginated<SimpleLanguage>> {
    return this.execute(this.listSimpleLanguagesRequest(translatedInItself, params))
      .notNull().decodePaginatedOf(SimpleLanguage);
  };

  public listAllSimpleLanguages(translatedInItself: boolean = false,
                                params: IListParameters<SimpleLanguage> = {}): Observable<SimpleLanguage[]> {
    return this.depaginateRequest(this.listSimpleLanguagesRequest(translatedInItself, params), SimpleLanguage);
  }

  public languageSelectOptions(
    translatedInItself: boolean = false,
    params: IListParameters<SimpleLanguage> = {}): Observable<readonly SelectOption<LanguageCode>[]> {
    return this.listAllSimpleLanguages(translatedInItself, {order: SortBy("name"), ...params})
      .pipe(mapToIdSelectOptions("name"));
  }

  public languageTranslations(languageCode: LanguageCode): Observable<Translations<LanguageTranslation>> {
    const request = RequestBuilder.get("languages", languageCode, "translations").authenticate().expectJsonObject();
    return this.execute(request).notNull().decodeTranslationsOf(LanguageTranslation);
  }

  public updateLanguageTranslations(
    languageCode: LanguageCode,
    translations: Readonly<Translations<LanguageTranslation>>,
  ): Observable<Translations<LanguageTranslation>> {
    const request = RequestBuilder.put(translations.toJson(), "languages", languageCode, "translations")
      .authenticate().expectJsonObject();
    return this.execute(request).notNull().decodeTranslationsOf(LanguageTranslation);
  }

  private listSimpleCountriesRequest(
    params: IPaginatedListParameters<SimpleCountry>): RequestBuilder<undefined, IJsonObject> {
    return RequestBuilder.get("countries", "simple").authenticate()
      .withPaginatedListParameters(params).expectJsonObject();
  }

  public listCountries(params: IPaginatedListParameters<Country> = {}): Observable<Paginated<Country>> {
    const request = RequestBuilder.get("countries")
      .authenticate().withPaginatedListParameters(params).expectJsonObject();
    return this.execute(request).notNull().decodePaginatedOf(Country);
  }

  public listSimpleCountries(params: IPaginatedListParameters<SimpleCountry> = {}): Observable<Paginated<SimpleCountry>> {
    return this.execute(this.listSimpleCountriesRequest(params)).notNull().decodePaginatedOf(SimpleCountry);
  }

  public listAllSimpleCountries(params: IListParameters<SimpleCountry> = {}): Observable<SimpleCountry[]> {
    return this.depaginateRequest(this.listSimpleCountriesRequest(params), SimpleCountry);
  }

  public countrySelectOptions(params: IListParameters<SimpleCountry> = {}): Observable<readonly SelectOption<CountryCode>[]> {
    return this.listAllSimpleCountries({order: SortBy("name"), ...params})
      .pipe(mapToIdSelectOptions("name"));
  }

  public createCountry(countryCode: CountryCode,
                       translations: Readonly<Translations<CountryTranslation>>): Observable<Country> {
    const request = RequestBuilder.post(translations.toJson(), "countries", countryCode)
      .authenticate().expectJsonObject();
    return this.execute(request).notNull().decodeJsonObjectOf(Country);
  }

  public countryTranslations(countryCode: CountryCode): Observable<Translations<CountryTranslation>> {
    const request = RequestBuilder.get("countries", countryCode, "translations").authenticate().expectJsonObject();
    return this.execute(request).notNull().decodeTranslationsOf(CountryTranslation);
  }

  public updateCountryTranslations(
    countryCode: CountryCode,
    translations: Readonly<Translations<CountryTranslation>>,
  ): Observable<Translations<CountryTranslation>> {
    const request = RequestBuilder.put(translations.toJson(), "countries", countryCode, "translations")
      .authenticate().expectJsonObject();
    return this.execute(request).notNull().decodeTranslationsOf(CountryTranslation);
  }
}
